import React, { useCallback, useEffect, useState } from 'react';
import {
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Box,
  Switch,
  Stack as MuiStack,
  TextField,
  Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LockIcon from '@mui/icons-material/Lock';

import Page from '@/components/Page';
import Card from '@/components/Card';
import Button from '@/components/Button';
import Stack from '@/components/Stack';
import LoadingState from '@/components/LoadingState';

import AppBinaryVersionPinModal from './AppBinaryVersionPinModal';
import {
  AppBinaryVersionPin,
  deleteAppBinaryVersionPin,
  fetchAppBinaryVersionPins,
  updateAppBinaryVersionPin,
} from '@/services/appBinaryVersionPinsAPI';

// Local-only gate to keep accidental edits off the binary kill-switch.
// Hardcoded per spec; ephemeral (cleared on tab close — not persisted).
// The real authorization is graphy's TenantAdmin role check on every
// request; this is a "you sure?" prompt, not security.
const BINARY_PIN_PASSWORD = 'gravydevs123';
const SESSION_STORAGE_KEY = 'app_binary_version_pins_unlocked';

const formatDate = (value: string | null) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

const PasswordGate = ({ onUnlock }: { onUnlock: () => void }) => {
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (value === BINARY_PIN_PASSWORD) {
      sessionStorage.setItem(SESSION_STORAGE_KEY, 'true');
      onUnlock();
    } else {
      setError('Incorrect password.');
    }
  };

  return (
    <Page title="App Binary Version Pins">
      <Box sx={{ mt: 2, maxWidth: 480 }}>
        <Card>
          <Card.Section>
            <Stack align="center" justify="apart">
              <Stack align="center" gap={8}>
                <LockIcon color="warning" />
                <Typography variant="h6">Restricted</Typography>
              </Stack>
            </Stack>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 1, mb: 2 }}
            >
              The binary pin controls the App Store / Play Store force-update
              gate. A wrong entry here can lock the entire worker fleet out of
              the app. Enter the developer password to continue.
            </Typography>

            <form onSubmit={handleSubmit}>
              <TextField
                label="Developer password"
                type="password"
                value={value}
                onChange={(e) => {
                  setValue(e.target.value);
                  if (error) setError(null);
                }}
                fullWidth
                size="small"
                autoFocus
                error={!!error}
                helperText={error}
                sx={{ mb: 2 }}
              />
              <Button
                a11yLabel="Unlock"
                label="Unlock"
                onClick={handleSubmit as unknown as () => void}
              />
            </form>
          </Card.Section>
        </Card>
      </Box>
    </Page>
  );
};

const AppBinaryVersionPins = () => {
  const [unlocked, setUnlocked] = useState<boolean>(
    () => sessionStorage.getItem(SESSION_STORAGE_KEY) === 'true',
  );

  const [pins, setPins] = useState<AppBinaryVersionPin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showModal, setShowModal] = useState(false);
  const [editingPin, setEditingPin] = useState<AppBinaryVersionPin | null>(
    null,
  );

  const loadPins = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetchAppBinaryVersionPins();
      setPins(response.data);
    } catch (err: any) {
      setError(
        err.response?.data?.errors?.join(', ') ||
          err.message ||
          'Failed to load pins',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (unlocked) loadPins();
  }, [unlocked, loadPins]);

  if (!unlocked) {
    return <PasswordGate onUnlock={() => setUnlocked(true)} />;
  }

  const openCreate = () => {
    setEditingPin(null);
    setShowModal(true);
  };

  const openEdit = (pin: AppBinaryVersionPin) => {
    setEditingPin(pin);
    setShowModal(true);
  };

  const handleDelete = async (pin: AppBinaryVersionPin) => {
    const confirmed = window.confirm(
      `Delete the ${pin.platform} binary pin? Devices on that platform will no longer be checked against an allow-list.`,
    );
    if (!confirmed) return;

    try {
      await deleteAppBinaryVersionPin(pin.id);
      loadPins();
    } catch (err: any) {
      setError(
        err.response?.data?.errors?.join(', ') ||
          err.message ||
          'Failed to delete pin',
      );
    }
  };

  const handleToggleGate = async (pin: AppBinaryVersionPin, next: boolean) => {
    setError(null);
    try {
      await updateAppBinaryVersionPin(pin.id, { gate_enabled: next });
      await loadPins();
    } catch (err: any) {
      setError(
        err.response?.data?.errors?.join(', ') ||
          err.message ||
          'Failed to toggle gate',
      );
    }
  };

  return (
    <Page title="App Binary Version Pins">
      <Box sx={{ mt: 2 }}>
        <Card>
          <Card.Section>
            <Stack align="center" justify="apart">
              <Typography variant="h6">
                Mobile Binary Force-Update Gate
              </Typography>
              <Button
                a11yLabel="Add binary version pin"
                label="Add Pin"
                onClick={openCreate}
              />
            </Stack>

            <Alert severity="warning" sx={{ mt: 1, mb: 2 }}>
              <Typography variant="body2">
                <strong>Publish to the App Store / Play Store FIRST</strong>,
                then add the new version. Adding a version that the Store has
                not yet approved locks the entire fleet on the force-update
                screen until the Store catches up.
              </Typography>
            </Alert>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Devices whose <code>Application.nativeApplicationVersion</code> is
              not in <code>supported_versions</code> for their platform are sent
              to the App Store / Play Store. Gate OFF or empty list ⇒ that
              platform skips enforcement entirely (safe escape hatch).
            </Typography>

            {loading ? (
              <LoadingState />
            ) : error ? (
              <Typography color="error">{error}</Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Platform</TableCell>
                      <TableCell align="center">Gate</TableCell>
                      <TableCell>Supported Versions</TableCell>
                      <TableCell>Last Synced</TableCell>
                      <TableCell>Notes</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pins.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          No binary pins yet. Add one for each platform once the
                          corresponding Store build is live.
                        </TableCell>
                      </TableRow>
                    ) : (
                      pins.map((pin) => (
                        <TableRow key={pin.id} hover>
                          <TableCell>
                            <Chip
                              label={pin.platform}
                              size="small"
                              color={
                                pin.platform === 'IOS' ? 'primary' : 'success'
                              }
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip
                              title={
                                pin.gate_enabled
                                  ? 'Gate ON — devices on an unlisted binary go to the Store'
                                  : 'Gate OFF — binary gate disabled for this platform'
                              }
                            >
                              <Switch
                                size="small"
                                checked={pin.gate_enabled}
                                onChange={(e) =>
                                  handleToggleGate(pin, e.target.checked)
                                }
                                color={pin.gate_enabled ? 'primary' : 'warning'}
                              />
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            {pin.supported_versions.length === 0 ? (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                — (no allow-list)
                              </Typography>
                            ) : (
                              <MuiStack spacing={0.25}>
                                {pin.supported_versions.map((v, idx) => (
                                  <Typography
                                    key={v}
                                    variant="body2"
                                    sx={{
                                      fontFamily: 'monospace',
                                      color:
                                        idx === 0
                                          ? 'text.primary'
                                          : 'text.secondary',
                                      fontWeight: idx === 0 ? 600 : 400,
                                    }}
                                  >
                                    {v}
                                  </Typography>
                                ))}
                              </MuiStack>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {formatDate(pin.last_synced_at)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {pin.notes || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => openEdit(pin)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(pin)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Card.Section>
        </Card>
      </Box>

      <AppBinaryVersionPinModal
        open={showModal}
        pin={editingPin}
        onClose={() => setShowModal(false)}
        onSaved={loadPins}
      />
    </Page>
  );
};

export default AppBinaryVersionPins;
